import { useState } from 'react'
import { Modal, ActionIcon, Tooltip, Anchor, Badge, Indicator, Alert } from '@mantine/core'
import { Package, AlertTriangle } from '@untitled-ui/icons-react'
import Shipment from '@/components/ui/Shipment'
import dayjs from '@/lib/dayjs'

const foStatuses = {
    OPEN: { label: 'À préparer', color: 'blue' },
    IN_PROGRESS: { label: 'En cours', color: 'orange' },
    CLOSED: { label: 'Terminé', color: 'green' },
    CANCELLED: { label: 'Annulé', color: 'red' },
}

export default function ServiceCaseShipments({ serviceCase }) {
    const [isOpen, setIsOpen] = useState(false)

    const shipments = serviceCase.shipments || []
    const fulfillmentOrders = serviceCase.fulfillment_orders || []
    const sortedShipments = [...shipments].sort((a, b) => {
        const aCancelled = !!a.canceled_at
        const bCancelled = !!b.canceled_at
        if (aCancelled !== bCancelled) return aCancelled ? 1 : -1
        return new Date(b.created_at) - new Date(a.created_at)
    })

    const activeFromId = serviceCase.from_customer_shipment?.id
    const activeToId = serviceCase.to_customer_shipment?.id
    const shipmentError = serviceCase.generate_shipment_error

    if (!shipments.length && !fulfillmentOrders.length && !shipmentError) return null

    const totalCount = shipments.length + fulfillmentOrders.length

    return (
        <>
            <Tooltip label={shipmentError ? 'Erreur de génération de bordereau' : `Expéditions (${totalCount})`}>
                <Indicator color="red" disabled={!shipmentError} size={8}>
                    <ActionIcon variant="default" size={36} onClick={() => setIsOpen(true)}>
                        <Package width={16} height={16} />
                    </ActionIcon>
                </Indicator>
            </Tooltip>

            <Modal
                opened={isOpen}
                onClose={() => setIsOpen(false)}
                title={`Expéditions (${totalCount})`}
                size="lg"
                centered
            >
                <div className="flex flex-col gap-4">
                    {shipmentError && (
                        <Alert
                            color="red"
                            icon={<AlertTriangle width={16} height={16} />}
                            title="Erreur de génération de bordereau"
                        >
                            {shipmentError}
                        </Alert>
                    )}

                    {fulfillmentOrders.length > 0 && (
                        <>
                            <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                                Bons de préparation
                            </span>
                            {fulfillmentOrders.map((fo) => (
                                <div
                                    key={fo.id}
                                    className="flex items-center justify-between border rounded-xl p-4"
                                >
                                    <div className="flex flex-col">
                                        <Anchor href={`/fulfillment_orders/${fo.id}`}>
                                            BP{fo.id}
                                        </Anchor>
                                        <span className="text-sm text-gray-400">
                                            Créé le {dayjs(fo.created_at).format('D MMM YYYY')}
                                        </span>
                                    </div>
                                    <Badge
                                        color={foStatuses[fo.status]?.color || 'gray'}
                                        variant="light"
                                    >
                                        {foStatuses[fo.status]?.label || fo.status}
                                    </Badge>
                                </div>
                            ))}
                        </>
                    )}

                    {sortedShipments.length > 0 && (
                        <>
                            {fulfillmentOrders.length > 0 && (
                                <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                                    Bordereaux
                                </span>
                            )}
                            {sortedShipments.map((shipment) => (
                                <Shipment
                                    key={shipment.id}
                                    shipment={shipment}
                                    selectedRole={
                                        shipment.id === activeFromId ? 'from_customer' :
                                        shipment.id === activeToId ? 'to_customer' :
                                        null
                                    }
                                />
                            ))}
                        </>
                    )}
                </div>
            </Modal>
        </>
    )
}
